/**
 * AppIntegrationsPanel — the side panel the Integrations section's Manage
 * button opens on the Apps page. It holds the app's four connection cards:
 * issue tracker, GitHub repository, Pipeline Integration and Slack channel.
 *
 * It used to be the app card's Integrations tab, which also listed every
 * variant under the GitHub, Slack and Pipeline cards. A variant is one level
 * below the app, so those lists moved onto the variant rows themselves
 * (`VariantIntegrationLinks`), and each card here says where they went.
 * The cards are otherwise unchanged.
 *
 * The data is not loaded here. `useAppIntegrations` owns it for the whole
 * app card, so the one-line summary updates the moment a card here saves.
 * The GitHub and Slack cards write their save's reply back through
 * `update`, and the issue tracker card reports its save and remove the
 * same way.
 *
 * **Disabled-picker empty state.** When the workspace has no GitHub (or
 * Slack) connection, the card says so and points at Workspace Settings →
 * Integrations to install the provider.
 *
 * **Partial-update semantics.** Each card's Save button commits ONLY that
 * provider's binding. The PATCH endpoint accepts a `{ github?, slack? }`
 * partial, so saving GitHub doesn't blow away the Slack state and vice
 * versa. Per-card busy state and per-card error, so a transient GitHub
 * failure doesn't disable the Slack card.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Loader2, MessageSquare, Workflow } from 'lucide-react'
import {
  Button,
  Label,
  Badge,
  SearchableSelect,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@/components/ui'
import { applicationsAPI, type AppIntegrationsResponse } from '@/lib/api'
import { AppIssueSourceCard } from './AppIssueSourceCard'
import { useAppContext } from '@/hooks/useAppContext'
import { GitHubIcon } from '@/auth/GitHubIcon'
import {
  APP_SLACK_EVENT_SUBSCRIPTIONS,
  type AppGitHubBinding,
  type AppSlackBinding,
  type AppSlackEventSubscription,
} from '@/types'
import { useAppIntegrations } from '@/pages/AppIntegrationsPanel/useAppIntegrations'

/** Customer-facing labels for each Slack event the app default can
 * subscribe to. Plain English on the left of the wire value, so the
 * checkbox list reads naturally to non-engineer operators. */
const SLACK_EVENT_LABELS: Record<
  AppSlackEventSubscription,
  { label: string; help?: string }
> = {
  'run.failed': {
    label: 'On failure',
    help: 'When a run completes with failing tests.',
  },
  'run.completed': {
    label: 'On completion',
    help: 'Every run, including healthy ones. Noisy on stable variants.',
  },
  'run.errored': {
    label: 'On error',
    help: 'When a run crashes before reporting tests (infra failure).',
  },
  'run.cancelled': {
    label: 'On cancellation',
    help: 'When a run is cancelled manually or by a queue trip.',
  },
}

/** Renders nothing until the Manage button (or an old link to the
 * Integrations tab) opens it. Mounted fresh on each open, so the card
 * forms start from the saved values every time. */
export function AppIntegrationsPanel() {
  const { panelOpen, closePanel, data } = useAppIntegrations()
  if (!panelOpen || !data) return null
  return (
    <Drawer open onOpenChange={(open) => !open && closePanel()}>
      <DrawerContent ariaLabel="Integrations for this app" data-testid="app-integrations-panel">
        <DrawerHeader title="Integrations" subtitle="Connections this app uses. Each variant's own settings are on its row." />
        <DrawerBody>
          <IntegrationCards data={data} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

function IntegrationCards({ data }: { data: AppIntegrationsResponse }) {
  const { appId, update } = useAppIntegrations()
  const { workspaceId } = useAppContext()
  // One workspace-scoped Integrations settings URL, shared by all three
  // "Open Workspace Settings" links in this panel (GitHub, Slack, Issue
  // tracker) so they can't drift. Built from the active workspace id so the
  // click lands on the right workspace's settings — the GitHub/Slack links
  // were once bare `?tab=...` hrefs that only mutated the Applications page's
  // tab query (Copilot review on #1160), and the issue-tracker card later
  // hardcoded a relative `../../settings/integrations` that dropped the
  // workspace entirely (#2306).
  const workspaceIntegrationsSettingsUrl = `/workspaces/${workspaceId}/settings?tab=workspace.integrations`
  // Per-provider save state — independent so a transient GitHub
  // failure doesn't visually freeze the Slack card.
  const [savingGitHub, setSavingGitHub] = useState(false)
  const [savingSlack, setSavingSlack] = useState(false)
  const [githubSaveError, setGitHubSaveError] = useState<string | null>(null)
  const [slackSaveError, setSlackSaveError] = useState<string | null>(null)
  const [githubSaveOk, setGitHubSaveOk] = useState(false)
  const [slackSaveOk, setSlackSaveOk] = useState(false)

  // Form state starts from the saved bindings; the user changes it and
  // clicks Save to commit. Kept apart from `data` so the form doesn't
  // snap back when the data is loaded again.
  const [githubForm, setGitHubForm] = useState<AppGitHubBinding>(() => data.bindings.github ?? {})
  const [slackForm, setSlackForm] = useState<AppSlackBinding>(() => data.bindings.slack ?? {})

  // ── Picker option lists (rendered to the SearchableSelect input) ──
  const githubRepoOptions = useMemo(() => {
    if (!data.github.connected) return []
    return data.github.repos.map((r) => ({
      value: r.repoIdentifier,
      label: r.repoIdentifier,
    }))
  }, [data])

  const slackChannelOptions = useMemo(() => {
    if (!data.slack.connected) return []
    return data.slack.channels.map((c) => ({
      value: c.id,
      // Prefix `#` (public) or 🔒 (private) so the operator can tell
      // the channel type at a glance — Slack's own UI uses this
      // convention. `name` already excludes the prefix.
      label: c.isPrivate ? `🔒 ${c.name}` : `# ${c.name}`,
    }))
  }, [data])

  async function saveGitHub() {
    setSavingGitHub(true)
    setGitHubSaveError(null)
    setGitHubSaveOk(false)
    try {
      const res = await applicationsAPI.updateIntegrations(appId, {
        // Send the whole subdoc — partial-field semantics are nice in
        // theory but the form is small enough that whole-subdoc
        // replacement on save is simpler and matches what the user
        // sees ("save this card's values").
        github: githubForm,
      })
      update((prev) => ({
        ...prev,
        bindings: { ...prev.bindings, github: res.bindings.github },
      }))
      setGitHubSaveOk(true)
    } catch (err) {
      setGitHubSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingGitHub(false)
    }
  }

  async function saveSlack() {
    setSavingSlack(true)
    setSlackSaveError(null)
    setSlackSaveOk(false)
    try {
      const res = await applicationsAPI.updateIntegrations(appId, {
        slack: slackForm,
      })
      update((prev) => ({
        ...prev,
        bindings: { ...prev.bindings, slack: res.bindings.slack },
      }))
      setSlackSaveOk(true)
    } catch (err) {
      setSlackSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingSlack(false)
    }
  }

  const githubConnected = data.github.connected
  const slackConnected = data.slack.connected

  return (
    <div className="space-y-4">
      {/* Issue-tracker binding card (#1558 Slice 1b) — the app's inbound
          requirement/defect source. Self-contained; consumes the
          issueSource binding + connectable trackers off the same
          getIntegrations response. */}
      <AppIssueSourceCard
        appId={appId}
        initialBinding={data.bindings.issueSource}
        connections={data.issueTracker.connections}
        settingsUrl={workspaceIntegrationsSettingsUrl}
        onBindingChange={(issueSource) =>
          update((prev) => ({ ...prev, bindings: { ...prev.bindings, issueSource } }))
        }
      />

      {/* GitHub binding card */}
      <section
        aria-labelledby="github-binding-heading"
        className="rounded-lg border bg-card p-4"
      >
        <header className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-[#1f2328] text-white">
            <GitHubIcon className="h-4 w-4" />
          </div>
          <h3
            id="github-binding-heading"
            className="text-sm font-semibold flex-1"
          >
            GitHub repository
          </h3>
          {githubConnected ? (
            data.bindings.github?.repoIdentifier ? (
              <Badge variant="default">Linked</Badge>
            ) : (
              <Badge variant="outline">Not linked</Badge>
            )
          ) : (
            // Name the provider explicitly so the badge doesn't read
            // as "your OttoTester workspace isn't connected" — the
            // workspace IS connected, GitHub specifically isn't.
            <Badge variant="outline">GitHub not connected</Badge>
          )}
        </header>

        {!githubConnected ? (
          <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
            <strong className="block text-foreground mb-1">
              GitHub isn’t connected on this workspace yet.
            </strong>
            Connect GitHub on the workspace to bind a repo to this app.{' '}
            <Link
              to={workspaceIntegrationsSettingsUrl}
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              Open Workspace Settings <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              Pulls from the workspace’s GitHub install. To change which GitHub
              org or installation backs this workspace, go to Workspace
              Settings → Integrations.
            </p>

            {data.github.connected && data.github.error && (
              <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200">
                {data.github.error} You can still keep the current selection.
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="github-repo-picker">Repository</Label>
                <SearchableSelect
                  id="github-repo-picker"
                  value={githubForm.repoIdentifier ?? ''}
                  onChange={(v) =>
                    setGitHubForm((prev) => ({
                      ...prev,
                      repoIdentifier: v === '' ? undefined : v,
                    }))
                  }
                  options={githubRepoOptions}
                  placeholder={
                    githubRepoOptions.length === 0
                      ? 'No repositories available'
                      : 'Select a repository'
                  }
                  searchPlaceholder="Search repos…"
                  disabled={githubRepoOptions.length === 0}
                  aria-label="GitHub repository"
                />
                <p className="text-[11px] text-muted-foreground">
                  Which repo events this app’s variants react to.
                </p>
              </div>

              {/*
               * #1184 (Slice 1 of meta #1183) trimmed two fields that
               * lived here previously:
               *   - "Default branch for new rules" text input — was
               *     leftover from the push / pull_request trigger era
               *     and has no role now that deployment_status is the
               *     only TriggerRule event type (#1173).
               *   - "Post Check Run on failure (app default)" toggle —
               *     Check Run posting is now a system-on default;
               *     disable-able per Replay template in the template
               *     editor, which already exists today.
               * No replacement UI at this layer — both behaviors moved
               * elsewhere as noted.
               */}
            </div>

            <div className="mt-4 flex items-center gap-3">
              {/* Save is enabled even when the picker has no options.
                  When the workspace's GitHub repo list failed to load
                  (transient API blip), the customer can still update
                  the default branch / toggle without picking a new
                  repo — the PATCH route accepts a partial subdoc.
                  Disabling the entire form on a picker-side blip
                  trapped the operator (Copilot review on #1160). */}
              <Button
                onClick={saveGitHub}
                disabled={savingGitHub}
                size="sm"
              >
                {savingGitHub && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Save
              </Button>
              {githubSaveError && (
                <span className="text-xs text-destructive">
                  {githubSaveError}
                </span>
              )}
              {githubSaveOk && !githubSaveError && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  Saved.
                </span>
              )}
            </div>
          </>
        )}

        {githubConnected && (
          <MovedToVariantRows>
            Trigger rules are set for each variant, from its row under Variants.
          </MovedToVariantRows>
        )}
      </section>

      {/* Pipeline Integration card — pull-style invocation (#1191,
        * Slice 2 of meta #1189). Sits BETWEEN GitHub (push trigger
        * above) and Slack (notifications below) so the configuration
        * flow reads top-to-bottom: deployment_status triggers →
        * pipeline triggers → notifications.
        *
        * Unlike GitHub / Slack, this card has no workspace-level
        * connection prerequisite — the customer's CI calls OttoTester
        * with a personal access token they generate themselves. The
        * card surfaces the per-variant drawer behind a "Configure"
        * affordance for each variant in the app. */}
      <PipelineIntegrationCard />

      {/* Slack binding card */}
      <section
        aria-labelledby="slack-binding-heading"
        className="rounded-lg border bg-card p-4"
      >
        <header className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-md bg-[#4a154b] text-white">
            <MessageSquare className="h-4 w-4" />
          </div>
          <h3
            id="slack-binding-heading"
            className="text-sm font-semibold flex-1"
          >
            Slack channel
          </h3>
          {slackConnected ? (
            data.bindings.slack?.defaultChannelId ? (
              <Badge variant="default">Linked</Badge>
            ) : (
              <Badge variant="outline">Not linked</Badge>
            )
          ) : (
            // Name the provider explicitly so the badge doesn't read
            // as "your OttoTester workspace isn't connected" — the
            // workspace IS connected, Slack specifically isn't.
            <Badge variant="outline">Slack not connected</Badge>
          )}
        </header>

        {!slackConnected ? (
          <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
            <strong className="block text-foreground mb-1">
              Slack isn’t connected on this workspace yet.
            </strong>
            Connect Slack on the workspace to bind a default channel to this
            app.{' '}
            <Link
              to={workspaceIntegrationsSettingsUrl}
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              Open Workspace Settings <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              Pulls from the workspace’s Slack install
              {data.slack.connected && data.slack.teamName ? (
                <>
                  {' '}
                  (<code>{data.slack.teamName}</code>)
                </>
              ) : null}
              . Variants can override per event.
            </p>

            {data.slack.connected && data.slack.error && (
              <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200">
                {data.slack.error} You can still keep the current selection.
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="slack-channel-picker">Default channel</Label>
                <SearchableSelect
                  id="slack-channel-picker"
                  value={slackForm.defaultChannelId ?? ''}
                  onChange={(v) =>
                    setSlackForm((prev) => ({
                      ...prev,
                      defaultChannelId: v === '' ? undefined : v,
                    }))
                  }
                  options={slackChannelOptions}
                  placeholder={
                    slackChannelOptions.length === 0
                      ? 'No channels available'
                      : 'Select a channel'
                  }
                  searchPlaceholder="Search channels…"
                  disabled={slackChannelOptions.length === 0}
                  aria-label="Slack default channel"
                />
                <p className="text-[11px] text-muted-foreground">
                  Where this app’s notifications go by default. Variants can
                  override per event.
                </p>
              </div>

              <div className="space-y-1">
                <Label>Notify on</Label>
                <ul className="space-y-1.5">
                  {APP_SLACK_EVENT_SUBSCRIPTIONS.map((ev) => {
                    const checked = (
                      slackForm.defaultEventSubscriptions ?? []
                    ).includes(ev)
                    const meta = SLACK_EVENT_LABELS[ev]
                    return (
                      <li key={ev} className="flex items-start gap-2">
                        <input
                          id={`slack-event-${ev}`}
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={(e) =>
                            setSlackForm((prev) => {
                              const current = prev.defaultEventSubscriptions ?? []
                              const next = e.target.checked
                                ? Array.from(new Set([...current, ev]))
                                : current.filter((x) => x !== ev)
                              return {
                                ...prev,
                                defaultEventSubscriptions: next,
                              }
                            })
                          }
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`slack-event-${ev}`}
                            className="cursor-pointer"
                          >
                            {meta.label}{' '}
                            <code className="text-[10px] text-muted-foreground font-mono">
                              {ev}
                            </code>
                          </Label>
                          {meta.help && (
                            <p className="text-[11px] text-muted-foreground">
                              {meta.help}
                            </p>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
                <p className="text-[11px] text-muted-foreground">
                  Override-able per variant.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              {/* See the GitHub Save above for the rationale — Save
                  stays enabled even when the channel picker has no
                  options so the customer can still adjust the event
                  subscriptions on a transient channel-list blip. */}
              <Button
                onClick={saveSlack}
                disabled={savingSlack}
                size="sm"
              >
                {savingSlack && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Save
              </Button>
              {slackSaveError && (
                <span className="text-xs text-destructive">{slackSaveError}</span>
              )}
              {slackSaveOk && !slackSaveError && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  Saved.
                </span>
              )}
            </div>
          </>
        )}

        {slackConnected && (
          <MovedToVariantRows>
            Slack overrides are set for each variant, from its row under Variants.
          </MovedToVariantRows>
        )}
      </section>

    </div>
  )
}

// ── MovedToVariantRows ──────────────────────────────────────────────
//
// Where a card's per-variant list used to be. The list moved onto the
// variant rows, so the card says where to find it.
function MovedToVariantRows({ children }: { children: ReactNode }) {
  return (
    <p className="mt-4 pt-4 border-t text-xs text-muted-foreground">{children}</p>
  )
}

// ── PipelineIntegrationCard ─────────────────────────────────────────
//
// Sits between the GitHub binding card and the Slack binding card, so the
// panel reads top to bottom: deployment triggers, pipeline triggers, then
// notifications (#1191, Slice 2 of meta #1189). Renders the pitch for the
// pull-style integration, the half that complements deployment_status push
// triggers.
//
// **No workspace-connection prerequisite.** Unlike the GitHub and Slack
// cards, this card always renders enabled: there is no workspace
// integration to install. The token the snippet uses is a personal access
// token the customer generates themselves on the Account Settings page,
// deep-linked from inside the drawer. Each variant's drawer opens from the
// Pipeline link on its row.
function PipelineIntegrationCard() {
  return (
    <section
      aria-labelledby="pipeline-integration-heading"
      className="rounded-lg border bg-card p-4"
      data-testid="pipeline-integration-card"
    >
      <header className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-to-br from-primary to-purple-600 text-white">
          <Workflow className="h-4 w-4" />
        </div>
        <h3
          id="pipeline-integration-heading"
          className="text-sm font-semibold flex-1"
        >
          Pipeline Integration
        </h3>
        <Badge variant="outline">Available</Badge>
      </header>

      <p className="text-xs text-muted-foreground">
        Run OttoTester from your own CI — GitHub Actions, GitLab,
        CircleCI, Jenkins, anything that can hit an HTTP API. Useful
        when your deploy platform doesn't post{' '}
        <code className="font-mono text-[11px] bg-muted/60 px-1 rounded">
          deployment_status
        </code>{' '}
        events, or when you want OttoTester to gate a release before
        the deploy itself.
      </p>

      <MovedToVariantRows>
        The pipeline is set up for each variant, from its row under Variants.
      </MovedToVariantRows>
    </section>
  )
}
